import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Mail, Lock, User as UserIcon, Building2, MapPin, Chrome, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { authLogin, authRegister } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAuth = (role) => {
  const redirect = `${window.location.origin}/auth/callback${role ? `?role=${role}` : ""}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

const errString = (d) =>
  !d ? "" : typeof d === "string" ? d : Array.isArray(d)
    ? d.map((e) => e?.msg || JSON.stringify(e)).join(" ")
    : d?.msg || JSON.stringify(d);

export const LoginPage = () => {
  const navigate = useNavigate();
  const { refreshUser, theme, toggleTheme } = useApp();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await authLogin(email, password);
      const u = await refreshUser();
      const target = u?.role === "employer" ? "/employer" : "/worker";
      navigate(location.state?.from || target, { replace: true });
    } catch (ex) {
      setErr(errString(ex.response?.data?.detail) || "Sign in failed");
    }
    setBusy(false);
  };

  return (
    <AuthShell theme={theme} onToggleTheme={toggleTheme} title="Welcome back" subtitle="Sign in to SkillBridge">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" icon={<Mail className="w-4 h-4" />}>
          <Input data-testid="login-email" type="email" required autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </Field>
        <Field label="Password" icon={<Lock className="w-4 h-4" />}>
          <Input data-testid="login-password" type="password" required autoComplete="current-password"
                 value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </Field>
        <div className="text-right -mt-2">
          <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-primary font-semibold hover:underline">Forgot password?</Link>
        </div>
        {err && <div data-testid="login-error" className="text-sm text-destructive">{err}</div>}
        <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Sign in
        </Button>
      </form>
      <OrDivider />
      <Button data-testid="google-signin-btn" variant="outline" className="w-full" onClick={() => startGoogleAuth()}>
        <Chrome className="w-4 h-4 mr-2" /> Continue with Google
      </Button>
      <p className="mt-6 text-sm text-center text-muted-foreground">
        New here? <Link to="/register" data-testid="go-register-link" className="text-primary font-semibold hover:underline">Create an account</Link>
      </p>
    </AuthShell>
  );
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { refreshUser, theme, toggleTheme } = useApp();
  const [step, setStep] = useState("role"); // role | form
  const [role, setRole] = useState("worker");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", location: "", headline: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await authRegister({ ...form, role });
      const u = await refreshUser();
      toast.success(`Welcome to SkillBridge, ${u?.name || ""}!`);
      navigate(role === "employer" ? "/employer" : "/worker", { replace: true });
    } catch (ex) {
      setErr(errString(ex.response?.data?.detail) || "Sign up failed");
    }
    setBusy(false);
  };

  return (
    <AuthShell theme={theme} onToggleTheme={toggleTheme} title="Create your account" subtitle="Join SkillBridge in under a minute">
      {step === "role" ? (
        <div className="space-y-3" data-testid="register-role-step">
          <p className="text-sm text-muted-foreground">I want to join as a…</p>
          <RoleCard testid="register-role-worker" title="Worker"
                    desc="Get verified skills, earn credentials, and be discovered." selected={role === "worker"} onClick={() => setRole("worker")} />
          <RoleCard testid="register-role-employer" title="Employer"
                    desc="Find, endorse and message proven talent." selected={role === "employer"} onClick={() => setRole("employer")} />
          <Button data-testid="register-continue-btn" className="w-full" onClick={() => setStep("form")}>Continue</Button>
          <OrDivider />
          <Button data-testid="register-google-btn" variant="outline" className="w-full" onClick={() => startGoogleAuth(role)}>
            <Chrome className="w-4 h-4 mr-2" /> Sign up with Google as {role}
          </Button>
          <p className="mt-4 text-sm text-center text-muted-foreground">
            Already have an account? <Link to="/login" data-testid="go-login-link" className="text-primary font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <Field label="Full name" icon={<UserIcon className="w-4 h-4" />}>
            <Input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
          </Field>
          <Field label="Email" icon={<Mail className="w-4 h-4" />}>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
          </Field>
          <Field label="Password" icon={<Lock className="w-4 h-4" />}>
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" />
          </Field>
          {role === "employer" && (
            <Field label="Company" icon={<Building2 className="w-4 h-4" />}>
              <Input data-testid="register-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Your company" />
            </Field>
          )}
          <Field label="Location" icon={<MapPin className="w-4 h-4" />}>
            <Input data-testid="register-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, Country" />
          </Field>
          <Field label={role === "worker" ? "Headline (what you do)" : "Role / Title"} icon={<ShieldCheck className="w-4 h-4" />}>
            <Input data-testid="register-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)}
                   placeholder={role === "worker" ? "e.g. Master Carpenter" : "e.g. Talent Lead"} />
          </Field>
          {err && <div data-testid="register-error" className="text-sm text-destructive">{err}</div>}
          <div className="flex gap-2">
            <Button data-testid="register-back-btn" type="button" variant="outline" onClick={() => setStep("role")}>Back</Button>
            <Button data-testid="register-submit" type="submit" disabled={busy} className="flex-1">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Create account
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

const AuthShell = ({ title, subtitle, children, theme, onToggleTheme }) => (
  <div className="min-h-screen grid lg:grid-cols-2 grain">
    <div className="hidden lg:flex flex-col justify-between p-10 bg-primary text-primary-foreground relative overflow-hidden">
      <div>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">SkillBridge</span>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight leading-tight">The trust layer for informal work.</h2>
        <p className="mt-4 text-base max-w-md text-primary-foreground/85">
          Real skills, verified. Connect with employers, message directly, earn endorsements and recommendations.
        </p>
      </motion.div>
      <div className="text-xs opacity-70">© SkillBridge</div>
    </div>
    <div className="flex flex-col justify-center px-6 sm:px-14 py-10 relative">
      <button data-testid="auth-theme-toggle" onClick={onToggleTheme}
              className="absolute top-6 right-6 text-xs text-muted-foreground hover:text-foreground">
        {theme === "dark" ? "☀ Light" : "☾ Dark"}
      </button>
      <div className="max-w-md w-full mx-auto">
        <h1 className="font-heading text-3xl font-black tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-1">{subtitle}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Field = ({ label, icon, children }) => (
  <div>
    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-1.5">
      {icon} {label}
    </Label>
    {children}
  </div>
);

const RoleCard = ({ testid, title, desc, selected, onClick }) => (
  <button data-testid={testid} onClick={onClick} type="button"
          className={`w-full text-left rounded-lg border-2 p-4 transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
    <div className="font-heading font-bold">{title}</div>
    <div className="text-sm text-muted-foreground">{desc}</div>
  </button>
);

const OrDivider = () => (
  <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
    <div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" />
  </div>
);
